import { EmbedMode, EmbedTheme } from "../../utils/embeds";
import { VerticalSpacing } from "../VerticalSpacing";
import { EmbedWrapper, V3EmbedArticleDescription, V3EmbedArticleImage, V3EmbedArticlePreview, V3EmbedArticleTitle } from "./V3EmbedContent.styles";
import { V3EmbedAuthor } from "./V3EmbedAuthor";
import {
    ArticlePreviewResponse,
    ContentResponse,
    PublicContentResponse,
    PublicUserResponse,
    ShareStatus,
    UserResponse,
    getItemDisplayTitle
} from "@authory-packages/core";
import { UnstyledLink } from "../ReusableStyledComponents/UnstyledLink";
import { getItemUrl, ItemUrlContext } from "../V3ProfileList/V3ProfileList.utils";
import { V3EmbedPoweredBy } from "./V3EmbedPoweredBy";
import { ItemMetadata } from "../ItemMetadata";
import { RequestHost } from "../../types/headers";
import { useContext } from "react";
import { DomainContext } from "../../context/domainContext";
import { isMainDomain } from "../../utils/domainRouting";

interface V3EmbedContentProps {
    embedTheme?: EmbedTheme;
    embedMode?: EmbedMode;
    author: UserResponse | PublicUserResponse;
    content: ContentResponse | PublicContentResponse;
    showHeader?: boolean;
}

export const V3EmbedContent = ({
    embedTheme = EmbedTheme.light,
    embedMode = EmbedMode.normal,
    author,
    content,
    showHeader = true
}: V3EmbedContentProps) => {
    const requestHostHeader: RequestHost = useContext(DomainContext) ?? RequestHost.MAINDOMAIN;
    const contentTargetLink = getItemUrl(
        content as unknown as ArticlePreviewResponse,
        author.slug,
        ItemUrlContext.content,
        undefined,
        isMainDomain(requestHostHeader)
    );

    const hasThumbnailImage = !!content.thumbnailImage;
    const haspreviewImage = !!content.previewImage;

    const headlineUseOneLine = embedMode === EmbedMode.normal || (embedMode === EmbedMode.compact && !hasThumbnailImage);

    // We leverage the custom background color feature to enforce white/dark background for embeds, which are the only ones supported
    const customBackgroundColor = embedTheme === EmbedTheme.light ? "#FFFFFF" : "#000000";

    const isAuthoryPagePublic = author.profileShareStatus === ShareStatus.Published;

    return (
        <EmbedWrapper embedTheme={embedTheme}>
            {showHeader && <V3EmbedAuthor author={author} embedTheme={embedTheme} customBackgroundColor={customBackgroundColor} />}
            <UnstyledLink href={isAuthoryPagePublic ? contentTargetLink : undefined} target="_blank">
                <VerticalSpacing bottom={18}>
                    <V3EmbedArticlePreview embedMode={embedMode}>
                        {hasThumbnailImage && haspreviewImage && (
                            <VerticalSpacing bottom={embedMode === EmbedMode.normal ? 18 : 0}>
                                <V3EmbedArticleImage
                                    $embedMode={embedMode}
                                    $imageSrc={embedMode === EmbedMode.normal ? content.previewImage : content.thumbnailImage}
                                />
                            </VerticalSpacing>
                        )}
                        <div>
                            <VerticalSpacing bottom={18}>
                                <V3EmbedArticleTitle
                                    $numLines={headlineUseOneLine ? 1 : 2}
                                    $embedTheme={embedTheme}
                                    $customBackgroundColor={customBackgroundColor}
                                >
                                    {getItemDisplayTitle(content)}
                                </V3EmbedArticleTitle>
                            </VerticalSpacing>
                            {!!content.description && (
                                <div>
                                    <V3EmbedArticleDescription $numLines={4} $embedTheme={embedTheme} $customBackgroundColor={customBackgroundColor}>
                                        {content.description}
                                    </V3EmbedArticleDescription>
                                </div>
                            )}
                        </div>
                    </V3EmbedArticlePreview>
                </VerticalSpacing>
            </UnstyledLink>
            <VerticalSpacing bottom={12}>
                <UnstyledLink href={isAuthoryPagePublic ? contentTargetLink : undefined} target="_blank">
                    <ItemMetadata
                        item={content}
                        customDateFormat={"MMM dd, yyyy"}
                        showRedirect={false}
                        isEmbed
                        embedTheme={embedTheme}
                        allowUserThemable
                        customBackgroundColor={customBackgroundColor}
                    />
                </UnstyledLink>
            </VerticalSpacing>
            <V3EmbedPoweredBy embedTheme={embedTheme} utmCampaign="article" />
        </EmbedWrapper>
    );
};
